import { useCallback, useMemo, useState } from 'react'

import { useIsManagedByVtex } from '~/modules/retailMedia/hooks/useIsManagedByVtex'
import { useAppSelector } from '~/store/hooks'

import * as goal from '../../elements/ObjectiveSteps/GoalSelection/step'
import * as linkInsertionOrder from '../../elements/ObjectiveSteps/LinkInsertionOrder/step'
import * as products from '../../elements/ObjectiveSteps/Products/step'
import * as setup from '../../elements/ObjectiveSteps/Setup/step'
import { useCampaignForm } from '../useCampaignForm'
import { pick } from './helpers'

export const useStepsValidation: UseStepsValidationProps = () => {
  const [validationData, setValidationData] = useState({} as ValidationData)
  const { isManagedByVtex } = useIsManagedByVtex()
  const { linkedInsertionOrder } = useCampaignForm()

  const handleValidationData = useCallback((data: ValidationData) => {
    setValidationData(prevState => ({
      ...prevState,
      ...data
    }))
  }, [])

  /**
   * Insertion Order (managed by VTEX only)
   */
  const campaignInsertionOrderIsValid = useMemo(
    () =>
      isManagedByVtex
        ? linkInsertionOrder.validator(linkedInsertionOrder)
        : true,
    [isManagedByVtex, linkedInsertionOrder]
  )

  const campaignInsertionOrder = useMemo(
    () => ({
      isValid: campaignInsertionOrderIsValid,
      isDisabled: false,
      title: linkInsertionOrder.labels.title,
      description:
        isManagedByVtex && linkedInsertionOrder
          ? linkInsertionOrder.labels.description.done(linkedInsertionOrder)
          : linkInsertionOrder.labels.description.pending
    }),
    [campaignInsertionOrderIsValid, isManagedByVtex, linkedInsertionOrder]
  )

  /**
   * Strategy
   */
  const strategyFields: ValidationData = useMemo(
    () => pick(validationData, goal.keys),
    goal.keys.map(key => validationData[key])
  )

  const campaignStrategyIsValid = useMemo(
    () => goal.validator(strategyFields),
    [strategyFields]
  )

  const campaignStrategy = useMemo(
    () => ({
      isValid: campaignStrategyIsValid,
      isDisabled: isManagedByVtex && !campaignInsertionOrderIsValid,
      title: goal.labels.title,
      description: campaignStrategyIsValid
        ? goal.labels.description.done(strategyFields.goal)
        : goal.labels.description.pending
    }),
    [
      campaignInsertionOrderIsValid,
      campaignStrategyIsValid,
      isManagedByVtex,
      strategyFields
    ]
  )

  /**
   * Products
   */
  const { selectedProducts } = useAppSelector(
    state => state.retailMedia.campaign.products
  )

  const campaignProductsIsValid = useMemo(
    () => products.validator(selectedProducts),
    [selectedProducts]
  )

  const campaignProducts = useMemo(
    () => ({
      isValid: campaignProductsIsValid,
      isDisabled:
        (isManagedByVtex && !campaignInsertionOrderIsValid) ||
        !campaignStrategyIsValid,
      title: products.labels.title,
      description: campaignProductsIsValid
        ? products.labels.description.done(selectedProducts?.length)
        : products.labels.description.initial
    }),
    [
      campaignInsertionOrderIsValid,
      campaignProductsIsValid,
      campaignStrategyIsValid,
      isManagedByVtex,
      selectedProducts
    ]
  )

  /**
   * Setup
   */
  const setupValues = useMemo(
    () => pick(validationData, setup.keys),
    setup.keys.map(key => validationData[key])
  )

  const campaignSetupIsValid = useMemo(
    () => setup.validator(setupValues),
    [setupValues]
  )
  const campaignSetup = useMemo(
    () => ({
      isValid: campaignSetupIsValid,
      isDisabled:
        (isManagedByVtex && !campaignInsertionOrderIsValid) ||
        !campaignStrategyIsValid ||
        !campaignProductsIsValid,
      title: setup.labels.title,
      description: setup.labels.description
    }),
    [
      campaignInsertionOrderIsValid,
      campaignProductsIsValid,
      campaignSetupIsValid,
      campaignStrategyIsValid,
      isManagedByVtex
    ]
  )

  /**
   * Full validation
   */

  const allStepsValid = useMemo(() => {
    const steps = {
      ...(isManagedByVtex ? { campaignInsertionOrder } : {}),
      campaignStrategy,
      campaignProducts,
      campaignSetup
    }

    return Object.values(steps).every(step => step.isValid && !step.isDisabled)
  }, [
    campaignInsertionOrder,
    campaignProducts,
    campaignSetup,
    campaignStrategy,
    isManagedByVtex
  ])

  return {
    handleValidationData,
    isManagedByVtex,
    campaignInsertionOrder,
    campaignStrategy,
    campaignProducts,
    campaignSetup,
    allStepsValid
  }
}
